import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { addCustomLabelsResourceBundle } from '../../shared/translations/i18nUtils';
import { isLandscape } from '../dashboardUtils';
import SsiFooter from '../../customizations/smartSort/SsiFooter';
import SsiHeader from '../../customizations/smartSort/SsiHeader';
import { LabelContextProvider } from '../../shared/hooks/labelContext';
import PrintViewHeader from './PrintViewHeader';
import WidgetPrintView from './printPreview/WidgetPrintView';
import { getLegendFormatter } from '../widgets/utils/dataFormatUtils';
import DashboardContext from '../../shared/hooks/dashboardContext';
import widgetDrawConfigurations from '../widgets/widgetDrawConfigurations';
import { setupViewAndWidgetConfigurations } from '../utils/configurationAndValidationUtils';
import ExportWidgetPanel from './ExportWidgetPanel';
import {
    addPrintOrientationStyles,
    areAllChartsLoaded,
    createCanvas,
    getDataFromLocalStorage,
    removeWebClientHeader,
} from './exportUtils';
import { FeatureTokenContextProvider } from '../../shared/hooks/featureTokensContext';

// eslint-disable-next-line complexity
export default function ExportDashboard(props) {
    const [state, setState] = useState();
    const [allChartsLoaded, setAllChartsLoaded] = useState(false);

    useEffect(() => {
        const handle = setInterval(
            () =>
                !!state &&
                areAllChartsLoaded(handle, setAllChartsLoaded, state),
            1000
        );
    }, [state]);

    useEffect(() => {
        window.allChartsLoaded = true;
    }, [allChartsLoaded]);

    useEffect(() => {
        const { viewConfiguration, metricData, thresholdValues, customLabels } =
            getDataFromLocalStorage(props.localStorageKey);

        addCustomLabelsResourceBundle(customLabels, props.siteLanguage);
        removeWebClientHeader();
        addPrintOrientationStyles(isLandscape(props.printViewMode));
        createCanvas();

        document.title = viewConfiguration.title;

        const allWidgetRenderers = {
            ...widgetDrawConfigurations,
            ...props.customizations?.widgetRenderers,
        };

        setState({
            viewConfiguration: setupViewAndWidgetConfigurations(
                allWidgetRenderers,
                viewConfiguration
            ),
            metricData,
            thresholdValues,
        });
    }, [
        props.localStorageKey,
        props.printViewMode,
        props.siteLanguage,
        props.customizations.widgetRenderers,
    ]);

    function renderWidgets(widgets, panelId, widgetWidth, widgetHeight) {
        return widgets.map((widget, index) => (
            <WidgetPrintView
                // eslint-disable-next-line react/no-array-index-key
                key={`${panelId}-${index}`}
                defaultFilterValues={state.viewConfiguration.filterValues}
                defaultTimeAndPrecision={
                    state.viewConfiguration.timeAndPrecision
                }
                defaultWhereClauses={state.viewConfiguration.whereClauses}
                defaultRecordLimit={{
                    recordLimit: state.viewConfiguration.recordLimit,
                    isDescending: !!state.viewConfiguration.isDescending,
                }}
                friendlyColumnLabels={
                    state.viewConfiguration.friendlyColumnLabels
                }
                metrics={state.metricData}
                printViewMode={props.printViewMode}
                widgetConfiguration={widget}
                widgetHeight={widgetHeight}
                widgetWidth={widgetWidth}
            />
        ));
    }

    function renderPanels() {
        return state.viewConfiguration.panels.map((panel) => {
            const panelWidgets = state.viewConfiguration.widgets.filter(
                (widget) => widget.panelId === panel.id
            );

            return (
                <ExportWidgetPanel
                    key={panel.id}
                    panel={panel}
                    panelWidgets={panelWidgets}
                    printViewMode={props.printViewMode}
                    renderWidgets={renderWidgets}
                />
            );
        });
    }

    if (!state) {
        return null;
    }

    const filterLabels = _.reduce(
        state.viewConfiguration.filterConfigurations,
        (result, filterValue, filterKey) => ({
            ...result,
            [filterKey]: filterValue?.label,
        }),
        state.viewConfiguration.friendlyColumnLabels
    );

    return (
        <LabelContextProvider siteLanguage={props.siteLanguage}>
            <FeatureTokenContextProvider>
                <DashboardContext.Provider
                    value={{
                        assets: props.assets,
                        thresholdValues: state.thresholdValues,
                        uriConstants: props.uriConstants,
                    }}
                >
                    <div
                        className={`dashboard-export-pdf dashboard-export-pdf--${props.printViewMode.toLowerCase()}`}
                    >
                        <PrintViewHeader
                            filterFormatter={getLegendFormatter(
                                [],
                                filterLabels
                            )}
                            filterValues={state.viewConfiguration.filterValues}
                            timeAndPrecision={
                                state.viewConfiguration.timeAndPrecision
                            }
                            subtitle={state.viewConfiguration.subtitle}
                            title={state.viewConfiguration.title}
                            whereClauses={state.viewConfiguration.whereClauses}
                        />
                        {state.viewConfiguration.showSsiWarning && (
                            <SsiHeader />
                        )}
                        <div className="dashboard-widgets">
                            {renderPanels()}
                        </div>
                        {state.viewConfiguration.showSsiWarning && (
                            <SsiFooter />
                        )}
                    </div>
                </DashboardContext.Provider>
            </FeatureTokenContextProvider>
        </LabelContextProvider>
    );
}

ExportDashboard.propTypes = {
    assets: PropTypes.instanceOf(Object),
    customizations: PropTypes.instanceOf(Object),
    localStorageKey: PropTypes.string.isRequired,
    printViewMode: PropTypes.string.isRequired,
    siteLanguage: PropTypes.string,
    uriConstants: PropTypes.instanceOf(Object).isRequired,
};

ExportDashboard.defaultProps = {
    assets: {},
    customizations: {},
    siteLanguage: 'en',
};
